import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import { UserKeys, bulkSetImap } from '../../../../api/users-api';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import Typography from '@mui/material/Typography';
interface ImapSettingsProps {
  open: boolean;
  handleClose: () => void;
  selectedUsers: string[];
  setSelectedUsers: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function ImapSettingsDialog({
  open,
  handleClose,
  selectedUsers,
  setSelectedUsers,
  changeLogRoles,
}: ImapSettingsProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending: isSaving } = useMutation({
    mutationFn: () =>
      bulkSetImap('User', selectedUsers, true, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'The task to update the user(s) IMAP settings has started.',
      });
      selectedUsers.forEach((user) => {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.ImapSettings, user],
        });
      });
      setChangeLogMessage('');
      setSelectedUsers([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating the user(s) IMAP settings. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_grid.bulk_set_imap_settings, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.name,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, UserRoles.BulkSetImap.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
      }}
      title="Enable IMAP"
      dialogContent={
        <>
          <Typography>
            {selectedUsers.length > 1 ? (
              <>
                Enable IMAP settings for{' '}
                <strong>{selectedUsers.length} users</strong>.
              </>
            ) : (
              <>
                Enable IMAP settings for <strong>{selectedUsers[0]}</strong>.
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isSaving}
          />
        </>
      }
      primaryButton={{
        loading: isSaving,
        action: handleSubmit,
        disabled: isSaving,
        title: 'Enable IMAP',
      }}
    />
  );
}
